// =============================================================================
// Design Detail Page — read-only staff inspection of a design (recovery view)
// -----------------------------------------------------------------------------
// Resolves a design by internal cuid or public DR_ reference via
// GET /api/v1/admin/designs/:idOrRef. Staff use this while investigating a
// PAID_ORDER_NO_DESIGN anomaly (incident SH-238897): it shows the design's
// parts with render statuses and asset links, the cart hand-off trace
// (cartBridgeAt), the lock state, and every order line already referencing
// the design. Linked from the Anomaly Center's candidate list.
// =============================================================================

import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import { Badge, Card, CardHeader, Spinner, type BadgeVariant } from '@tkpod/ui';

// ---------------------------------------------------------------------------
// Types (mirror the admin design detail API)
// ---------------------------------------------------------------------------

interface DesignPartView {
  id: string;
  partKey: string;
  kind: string;
  widthIn: number;
  heightIn: number;
  exportWidthIn: number | null;
  exportHeightIn: number | null;
  previewStatus: string;
  exportStatus: string;
  mockupStatus: string;
  previewUrl: string | null;
  exportUrl: string | null;
  mockupUrl: string | null;
}

interface DesignOrderItemView {
  id: string;
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  title: string;
  quantity: number;
  partKey: string | null;
}

interface DesignDetail {
  id: string;
  designRefId: string | null;
  title: string | null;
  mode: string;
  context: string;
  status: string;
  revision: number;
  schemaVersion: number;
  isLocked: boolean;
  lockedAt: string | null;
  lockedByOrderId: string | null;
  archivedAt: string | null;
  cartBridgeAt: string | null;
  blankVariantId: string | null;
  previewUrl: string | null;
  organization: { id: string; name: string };
  createdAt: string;
  updatedAt: string;
  parts: DesignPartView[];
  orderItems: DesignOrderItemView[];
}

const ASSET_STATUS_VARIANT: Record<string, BadgeVariant> = {
  READY: 'success',
  PENDING: 'default',
  PROCESSING: 'warning',
  FAILED: 'danger',
};

const DESIGN_STATUS_VARIANT: Record<string, BadgeVariant> = {
  DRAFT: 'default',
  ORDERED: 'info',
  LOCKED: 'warning',
  ARCHIVED: 'default',
};

function AssetStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={ASSET_STATUS_VARIANT[status] ?? 'default'} size="sm">
      {status}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DesignDetailPage() {
  const { idOrRef } = useParams<{ idOrRef: string }>();
  const api = useApiClient();

  const designQuery = useQuery({
    queryKey: ['admin', 'designs', idOrRef],
    queryFn: () =>
      api.get<{ data: DesignDetail }>(`/api/v1/admin/designs/${idOrRef}`),
    enabled: !!idOrRef,
    retry: false,
  });

  if (designQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (designQuery.isError || !designQuery.data) {
    return (
      <Card>
        <p className="px-5 py-8 text-center text-sm text-danger">
          {designQuery.error instanceof Error &&
          designQuery.error.message.length > 0
            ? designQuery.error.message
            : 'Design not found.'}
        </p>
      </Card>
    );
  }

  const design = designQuery.data.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">
            {design.title ?? design.designRefId ?? design.id}
          </h1>
          <p className="font-mono text-xs text-text-secondary">
            {design.designRefId ?? design.id}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge
            variant={DESIGN_STATUS_VARIANT[design.status] ?? 'default'}
            size="sm"
          >
            {design.status}
          </Badge>
          <Badge variant="info" size="sm">
            {design.mode}
          </Badge>
          <Badge variant="default" size="sm">
            {design.context}
          </Badge>
          {design.isLocked && (
            <Badge variant="warning" size="sm">
              Locked
            </Badge>
          )}
          {design.archivedAt && (
            <Badge variant="default" size="sm">
              Archived
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          {/* Parts */}
          <Card padding="none">
            <CardHeader title={`Parts (${design.parts.length})`} />
            {design.parts.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-text-secondary">
                This design has no parts — it was never processed by the part
                extractor.
              </p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
                  <tr>
                    <th className="px-5 py-2 font-semibold">Part</th>
                    <th className="px-5 py-2 font-semibold">Kind</th>
                    <th className="px-5 py-2 font-semibold">Size</th>
                    <th className="px-5 py-2 font-semibold">Preview</th>
                    <th className="px-5 py-2 font-semibold">Export</th>
                    <th className="px-5 py-2 font-semibold">Mockup</th>
                    <th className="px-5 py-2 text-right font-semibold">
                      Files
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {design.parts.map((part) => (
                    <tr
                      key={part.id}
                      className="border-b border-border/60 align-top"
                    >
                      <td className="px-5 py-3 font-mono text-xs text-text-primary">
                        {part.partKey}
                      </td>
                      <td className="px-5 py-3 text-xs text-text-secondary">
                        {part.kind}
                      </td>
                      <td className="px-5 py-3 text-xs text-text-secondary">
                        {part.widthIn}&quot; × {part.heightIn}&quot;
                        {part.exportWidthIn != null &&
                          part.exportHeightIn != null && (
                            <span className="block text-text-tertiary">
                              print {part.exportWidthIn}&quot; ×{' '}
                              {part.exportHeightIn}&quot;
                            </span>
                          )}
                      </td>
                      <td className="px-5 py-3">
                        <AssetStatusBadge status={part.previewStatus} />
                      </td>
                      <td className="px-5 py-3">
                        <AssetStatusBadge status={part.exportStatus} />
                      </td>
                      <td className="px-5 py-3">
                        <AssetStatusBadge status={part.mockupStatus} />
                      </td>
                      <td className="px-5 py-3 text-right text-xs">
                        <div className="flex justify-end gap-2">
                          {part.previewUrl && (
                            <a
                              href={part.previewUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-brand-600 hover:underline"
                            >
                              Preview
                            </a>
                          )}
                          {part.exportUrl && (
                            <a
                              href={part.exportUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-brand-600 hover:underline"
                            >
                              300 DPI
                            </a>
                          )}
                          {part.mockupUrl && (
                            <a
                              href={part.mockupUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-brand-600 hover:underline"
                            >
                              Mockup
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Referencing order lines */}
          <Card padding="none">
            <CardHeader title={`Order lines (${design.orderItems.length})`} />
            {design.orderItems.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-text-secondary">
                No order line references this design — it is an orphan. Attach
                it from the affected order&apos;s detail page.
              </p>
            ) : (
              <div className="divide-y divide-border">
                {design.orderItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-3 px-5 py-3"
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/orders/${item.orderId}`}
                        className="text-sm font-medium text-brand-600 hover:underline"
                      >
                        {item.orderNumber}
                      </Link>
                      <p className="truncate text-xs text-text-tertiary">
                        {item.title} × {item.quantity}
                        {item.partKey ? ` • ${item.partKey}` : ''}
                      </p>
                    </div>
                    <Badge variant="default" size="sm">
                      {item.orderStatus}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {design.previewUrl && (
            <Card>
              <CardHeader title="Preview" />
              <div className="px-5 pb-5">
                <img
                  src={design.previewUrl}
                  alt="Design preview"
                  className="w-full rounded-md border border-border object-contain"
                />
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title="Details" />
            <dl className="space-y-2 px-5 pb-5 text-xs">
              <MetaRow label="Organization" value={design.organization.name} />
              <MetaRow label="Revision" value={String(design.revision)} />
              <MetaRow
                label="Schema version"
                value={String(design.schemaVersion)}
              />
              <MetaRow
                label="Created"
                value={new Date(design.createdAt).toLocaleString()}
              />
              <MetaRow
                label="Updated"
                value={new Date(design.updatedAt).toLocaleString()}
              />
              <MetaRow
                label="Cart hand-off"
                value={
                  design.cartBridgeAt
                    ? new Date(design.cartBridgeAt).toLocaleString()
                    : 'Never reached cart'
                }
              />
              {design.blankVariantId && (
                <MetaRow
                  label="Blank variant"
                  value={design.blankVariantId}
                  mono
                />
              )}
              {design.isLocked && design.lockedByOrderId && (
                <div className="flex items-start justify-between gap-2">
                  <dt className="shrink-0 text-text-tertiary">Locked by</dt>
                  <dd className="text-right">
                    <Link
                      to={`/orders/${design.lockedByOrderId}`}
                      className="font-mono text-brand-600 hover:underline"
                    >
                      {design.lockedByOrderId}
                    </Link>
                    {design.lockedAt && (
                      <span className="block text-text-tertiary">
                        {new Date(design.lockedAt).toLocaleString()}
                      </span>
                    )}
                  </dd>
                </div>
              )}
              <MetaRow label="Internal id" value={design.id} mono />
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}

function MetaRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <dt className="shrink-0 text-text-tertiary">{label}</dt>
      <dd
        className={`break-all text-right text-text-primary ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </dd>
    </div>
  );
}
